import styles from './Rental.module.css';

import type { RentalOnchainAction } from 'src/utils/rentalActions';

import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import { useParams, useNavigate } from 'react-router';
import { X, Car, ArrowCounterClockwise } from '@phosphor-icons/react';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useQueryScreenState } from 'src/hooks/useQueryScreenState';

import { resolveDealChain } from 'src/utils/dealChain';
import { formatDuration } from 'src/utils/rentalClock';
import { formatImageUrl } from 'src/utils/format-image';
import { formatDay, formatDateTime } from 'src/utils/date';
import { noPhotoAlt, carPhotoAlt } from 'src/utils/imageAlt';
import { buildRentalTracker } from 'src/utils/rentalTracker';
import { carTitle as carNameOf } from 'src/utils/documentTitle';
import { rentalToken, formatBaseUnits, rentalInvoiceView } from 'src/utils/rentalInvoice';
import {
  isRentalTerminal,
  rentalDisputeView,
  rentalContractCall,
  rentalDealFingerprint,
  rentalActionVisibility,
} from 'src/utils/rentalActions';

import { useRental, useBuyerChains } from 'src/queries';
import { useWalletUI, WalletButton } from 'src/blockchain';
import currencyImage from 'src/assets/images/currency.png';

import { Modal } from 'src/components/Modal';
import { Loader } from 'src/components/Loader';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { DealTracker } from 'src/components/DealTracker';
import { InvoiceCard } from 'src/components/InvoiceCard';
import { Typography } from 'src/components/themed/Typography';
import { RentalPayModal } from 'src/components/RentalPayModal';
import { RequestFailureState } from 'src/components/EmptyState';
import { ActionButton } from 'src/components/themed/ActionButton';
import { RentalSplitModal } from 'src/components/RentalSplitModal';
import { RentalTopUpModal } from 'src/components/RentalTopUpModal';
import { RentalExtendModal } from 'src/components/RentalExtendModal';
import { TxProgress, useTxProgress, useClearWhenWalletConnects } from 'src/components/TxProgress';

/**
 * The renter's rental (contracts v3, UI part §2–§5).
 *
 * The same three components the delivery deal is built from — the tracker says where the rental
 * is, the invoice says what it cost and proves what was paid, and `TxProgress` narrates every
 * signature — over the rental's own actions: pay, accept, extend, top up, return, release, and
 * the two halves of a disputed deposit.
 *
 * Everything chain-shaped comes from `deal.chainKey`: which wallet family signs, which network the
 * wallet is switched to before an irreversible transfer, which stablecoin the amounts are in and
 * which explorer a hash links to. The screen holds no opinion about any of it, which is what lets
 * one screen serve a legacy TRON rental in USDT and a Base one in EURC.
 */

// "2d 5h" / "3h" / "12m" — coarse countdown until the deposit unlocks.
const formatRemaining = (seconds: number): string => {
  if (seconds <= 0) return '0m';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

interface ConfirmCopy {
  header: string;
  body: string;
  button: string;
  danger?: boolean;
}

/**
 * What each confirmation says. Kept next to the buttons rather than in the rules module: these are
 * the sentences a renter reads before signing, and every one of them names the money that moves.
 */
const confirmCopy = (
  action: RentalOnchainAction,
  ctx: { car: string; claim: string; refundOnApprove: string; deposit: string; agree: string }
): ConfirmCopy => {
  switch (action) {
    case 'start':
      return {
        header: 'Confirm you received the car',
        body: `Confirm only once you have taken possession of the ${ctx.car}. This starts the rental period and releases the rental amount to the dealer. Your deposit stays in escrow.`,
        button: 'Accept the car',
      };
    case 'return':
      return {
        header: 'Return the car',
        body: 'Confirm once you have handed the car back. This opens a seven-day inspection window, after which the deposit that is left comes back to you.',
        button: 'Return the car',
      };
    case 'release':
      return {
        header: 'Release your deposit?',
        body: `The inspection window is over and nothing was claimed. ${ctx.deposit} goes back to your wallet and the rental closes.`,
        button: 'Release deposit',
      };
    case 'cancel':
      return {
        header: 'Cancel this rental?',
        body: 'The rental and the deposit come back to your wallet; the platform fee is not refunded. Only possible before you accept the car. This is an on-chain action.',
        button: 'Yes, cancel & refund',
        danger: true,
      };
    case 'approveDefect':
      return {
        header: 'Accept the charge',
        body: `Accepting sends ${ctx.claim} to the dealer and returns ${ctx.refundOnApprove} to you. This completes the rental and cannot be undone.`,
        button: 'Accept & split deposit',
      };
    case 'disputeDefect':
      return {
        header: 'Dispute the charge',
        body: `Disputing holds your ${ctx.deposit} deposit until the claim is settled. From then on it takes two of the three parties — you, the dealer and GAC — to agree on a split, and after 14 days you can take the rest yourself.`,
        button: 'Dispute this charge',
        danger: true,
      };
    case 'claimStale':
      return {
        header: 'Claim the remaining deposit?',
        body: `The dispute has been open for 14 days without an agreed split, so the contract lets you take what is left — ${ctx.deposit} — alone. This closes the rental.`,
        button: 'Claim deposit',
      };
    case 'agreeSplit':
      return {
        header: 'Agree to this split?',
        body: `Proposing the same number is what settles the dispute: the dealer keeps ${ctx.agree} and the rest of the deposit comes back to you. It executes in this transaction.`,
        button: 'Agree & settle',
      };
    default:
      return { header: 'Confirm', body: 'This is an on-chain action.', button: 'Confirm' };
  }
};

export const Rental = () => {
  const { id: rentalId } = useParams();
  const navigate = useNavigate();

  const rentalQuery = useRental(rentalId!);
  const { data: item, refetch } = rentalQuery;
  const screenState = useQueryScreenState(rentalQuery);
  const { data: buyerChains } = useBuyerChains();
  useDocumentTitle(item ? carNameOf(item.listing) : undefined, 'Rental');

  const [payVisible, setPayVisible] = useState(false);
  const [extendVisible, setExtendVisible] = useState(false);
  const [topUpVisible, setTopUpVisible] = useState(false);
  const [splitVisible, setSplitVisible] = useState(false);
  const [confirmAction, setConfirmAction] = useState<RentalOnchainAction | null>(null);
  // The amount an "agree" confirmation is about — the other party's proposal, carried to the call.
  const [agreeAmount, setAgreeAmount] = useState<bigint | null>(null);
  // Kept past the dialog so a failed signature can be offered again by name.
  const [lastAction, setLastAction] = useState<RentalOnchainAction | null>(null);
  const [actionError, setActionError] = useState('');
  const [extensionDeclined, setExtensionDeclined] = useState(false);
  // What the deal looked like when a signature went out. The progress panel stays up until the
  // deal actually differs — ending at "confirmed" would show success over a screen still showing
  // the old state, which is the moment a renter presses the same button twice.
  const [signedFrom, setSignedFrom] = useState<string | null>(null);
  // The countdowns tick locally; every amount they sit next to still comes from the chain.
  const [nowSeconds, setNowSeconds] = useState(() => Math.floor(Date.now() / 1000));

  const chain = useMemo(
    () =>
      item
        ? resolveDealChain({
            chainKey: item.chainKey,
            buyerChains,
            listingCurrency: item.listing?.currency,
          })
        : null,
    [item, buyerChains]
  );

  const walletUI = useWalletUI(chain?.family);
  const tx = useTxProgress();

  useClearWhenWalletConnects(tx, chain?.family);

  const terminal = item ? isRentalTerminal(item.status) : false;

  useEffect(() => {
    const tick = setInterval(() => setNowSeconds(Math.floor(Date.now() / 1000)), 1000);

    return () => clearInterval(tick);
  }, []);

  // While a rental is live the on-chain state is mirrored by the poller — refetch so every
  // signature surfaces its resulting status without a manual reload.
  useEffect(() => {
    if (!item || terminal) {
      return undefined;
    }
    const interval = setInterval(() => refetch(), 3000);

    return () => clearInterval(interval);
  }, [item, terminal, refetch]);

  const state = item?.rentalContractState ?? null;

  // The pending plate disappearing is the only on-screen trace of the dealer's decision, so watch
  // the poll stream: request gone + extensionTotal unchanged = declined (an approval shows the
  // persistent "extended until" plate instead).
  const prevExtensionRef = useRef<{ pending: string; total: string } | null>(null);
  useEffect(() => {
    if (!state) {
      return;
    }
    const prev = prevExtensionRef.current;

    if (prev && prev.pending !== '0' && state.pendingExtensionPeriod === '0') {
      setExtensionDeclined(state.extensionTotal === prev.total);
    }
    prevExtensionRef.current = { pending: state.pendingExtensionPeriod, total: state.extensionTotal };
  }, [state]);

  const handleClose = useCallback(() => navigate('/rent', { replace: true }), [navigate]);

  /** Renter-signed contract calls: everything that is one confirmation and one signature. */
  const handleOnchainAction = async () => {
    if (!item || !confirmAction) {
      return;
    }

    const contractAddress = state?.address ?? item.contractAddress ?? '';

    if (!contractAddress) {
      setActionError('The escrow contract is not ready yet. Please try again shortly.');

      return;
    }

    const call = rentalContractCall(confirmAction, agreeAmount ?? undefined);

    setActionError('');
    setConfirmAction(null);
    setLastAction(confirmAction);
    setSignedFrom(rentalDealFingerprint(item));

    await tx.run(
      (onProgress) =>
        walletUI.callContract({
          contractAddress,
          signature: call.signature,
          args: call.args,
          chainId: chain?.chainId,
          onProgress,
        }),
      'Transaction failed. Please try again.'
    );

    refetch();
  };

  if (screenState === 'loading') {
    return (
      <div className={classNames('container', styles.container)}>
        <Loader />
      </div>
    );
  }

  if (screenState !== 'ready' || !item) {
    // A 404 or a 500 used to leave a blank page (app#210).
    return (
      <div className={classNames('container', styles.container)}>
        <Stack width="100%" p={4} pb={20} maxWidth="750px">
          <RequestFailureState
            titleAs="h1"
            kind={screenState === 'ready' ? 'failed' : screenState}
            subject="This rental"
            onBack={handleClose}
            backLabel="Back to deals"
            onRetry={() => refetch()}
          />
        </Stack>
      </div>
    );
  }

  const vis = rentalActionVisibility(item, nowSeconds);
  const dispute = rentalDisputeView(item, nowSeconds * 1000);
  const tracker = buildRentalTracker(item, chain, nowSeconds * 1000);
  const invoice = rentalInvoiceView(item, chain);
  const token = rentalToken(item, chain);
  const { clock } = vis;

  const money = (value: bigint) => `${formatBaseUnits(value, token.decimals)} ${token.symbol}`;
  const defectClaim = state ? BigInt(state.defectAmount || '0') : 0n;
  const remainingDeposit = state ? BigInt(state.remainingDeposit || '0') : 0n;
  const refundOnApprove = remainingDeposit > defectClaim ? remainingDeposit - defectClaim : 0n;

  const carTitle = carNameOf(item.listing);

  // Approved extensions accumulate in `extensionTotal`; the off-chain booking dates are NOT shifted
  // by the contract, so the real end date must come from the on-chain `rentalEnd`.
  const extensionApproved = !!state && state.extensionTotal !== '0';
  const onChainEnd = state ? dayjs(Number(state.rentalEnd) * 1000) : null;

  // The signature landed and the deal has moved on. "Moved on" is not the status alone: a top-up
  // changes only the remaining deposit and a first proposal only one of three numbers, so a
  // status-only comparison would spin on "updating your deal" over a deal that had already updated.
  const syncDone = tx.state?.phase === 'syncing' && rentalDealFingerprint(item) !== signedFrom;
  // Every on-chain button is disabled while one signature is in flight — the second click is the
  // one that costs a network fee for a transaction that reverts.
  const busy = tx.isRunning && !syncDone;
  // Nothing on-chain can be signed before the deal's network is known: the wallet would have to be
  // switched to a chain id nobody resolved, and this build may not even carry that family.
  const canSignOnchain = !!chain?.signable && !!(state?.address ?? item.contractAddress);

  const confirm = confirmAction
    ? confirmCopy(confirmAction, {
        car: carTitle,
        claim: money(defectClaim),
        refundOnApprove: money(refundOnApprove),
        deposit: money(remainingDeposit),
        agree: agreeAmount !== null ? money(agreeAmount) : '—',
      })
    : null;

  const ask = (action: RentalOnchainAction, amount: bigint | null = null) => {
    setAgreeAmount(amount);
    setConfirmAction(action);
  };

  return (
    <div className={classNames('container', styles.container)}>
      <Stack width="100%" gap={2} p={4} pb={24} maxWidth="750px">
        <Stack width="100%" flexDirection="row" justifyContent="space-between" alignItems="center" pl={4}>
          <Typography as="h1" fontWeight="600" variant="title3" color="white">
            Rental details
          </Typography>
          <Stack flexDirection="row" gap={2} alignItems="center">
            <ActionButton onClick={refetch} label="Refresh the rental">
              <ArrowCounterClockwise size={22} color="white" />
            </ActionButton>
            <ActionButton onClick={handleClose} label="Back to deals">
              <X size={24} color="white" />
            </ActionButton>
          </Stack>
        </Stack>

        <Stack width="100%" gap={4}>
          <ListItem
            title={carTitle}
            description={item.listing.vin ?? undefined}
            image={
              !isEmpty(item.listing.images) ? (
                <img src={formatImageUrl(item.listing.images[0])} alt={carPhotoAlt(carTitle)} className={styles.image} />
              ) : (
                <img src={currencyImage} alt={noPhotoAlt(carTitle)} className={classNames([styles.image, styles.defaultImage])} />
              )
            }
          >
            <Stack width="100%" gap={1}>
              <div className={styles.row}>
                <Typography color="white">Period:</Typography>
                <Typography color="lightWhite">
                  {formatDay(item.startDate)} —{' '}
                  {extensionApproved && onChainEnd
                    ? `${formatDay(onChainEnd)} (extended)`
                    : formatDay(item.endDate)}
                </Typography>
              </div>
            </Stack>
          </ListItem>

          <DealTracker tracker={tracker} chain={chain} />

          {/* The plates below are the live clock: what is running out, what has started costing
              money, and what has to be paid before the dealer can act. Everything they quote is
              read from the on-chain mirror. */}
          {extensionApproved ? (
            <Plate tone="success">Rental extended until {formatDateTime(onChainEnd!)}</Plate>
          ) : null}

          {extensionDeclined && !vis.extensionPending ? (
            <Plate tone="danger">Extension declined by the dealer — nothing was charged</Plate>
          ) : null}

          {vis.payDeadlinePassed && !state?.isPaid ? (
            <Plate tone="danger">Payment window closed — this booking is being cancelled and its dates freed</Plate>
          ) : null}

          {vis.bookingExpired ? (
            <Plate tone="danger">
              The booked period has ended and the car was never handed over — the payment comes back to you.
            </Plate>
          ) : null}

          {vis.canReturn && !clock.isOverdue && !clock.isInGrace && clock.secondsToEnd > 0 ? (
            <Plate tone="muted">
              Ends in {formatDuration(clock.secondsToEnd)}
              {clock.isDailyModel && clock.dailyRate > 0n
                ? ` — after that every started day costs ${money(clock.dailyRate)} from your deposit`
                : ''}
            </Plate>
          ) : null}

          {clock.isInGrace ? (
            <Plate tone="warning">
              Rental ended. Return the car within {formatDuration(clock.secondsToFirstCharge)} or the first day starts
              {clock.isDailyModel && clock.dailyRate > 0n ? ` at ${money(clock.dailyRate)} from your deposit` : ''}
            </Plate>
          ) : null}

          {clock.isOverdue ? (
            <Plate tone={clock.isExhausted ? 'danger' : 'warning'}>
              {clock.isExhausted
                ? `Deposit exhausted: ${money(clock.chargedAmount)} charged for days past the end date${
                    clock.outstandingOverdue > 0n ? `, ${money(clock.outstandingOverdue)} still owed` : ''
                  }. Return the car now — the dealer may escalate.`
                : clock.isDailyModel
                  ? `Past the end date: day ${Math.max(clock.startedDays, clock.chargedDays)}, ${money(
                      clock.chargedAmount
                    )} charged from your deposit, ${money(clock.remainingDeposit)} left`
                  : 'Past the end date — the car is still out and your deposit is being charged'}
            </Plate>
          ) : null}

          {clock.isOverdue && !clock.isExhausted && clock.hasUnchargedDay ? (
            <Plate tone="muted">
              Day {clock.startedDays} has started and is charged shortly — the amount above updates once the charge is
              on-chain.
            </Plate>
          ) : null}

          {clock.isOverdue && clock.isExhausted && clock.outstandingOverdue > 0n ? (
            <Plate tone="muted">
              Your deposit could not cover the whole of a day past the end date, so {money(clock.outstandingOverdue)}{' '}
              stays owed. Paying it settles the rental and lets you extend again.
            </Plate>
          ) : null}

          <InvoiceCard
            lines={invoice.lines}
            total={invoice.total}
            footnote={invoice.footnote}
            issuedAt={invoice.issuedAt}
            issuedFallback={invoice.issuedFallback}
            paymentTxHash={invoice.paymentTxHash}
            chain={chain}
            meta={invoice.meta}
          />

          {/* CC2: the dealer claimed a defect and the renter answers it. */}
          {vis.defectPending ? (
            <Stack gap={1} className={styles.section}>
              <Typography color="white" fontWeight="600">
                The dealer reported a defect
              </Typography>
              <Typography color="lightWhite" variant="small">
                They claim {money(defectClaim)} from your deposit of {money(remainingDeposit)}. Accepting pays them and
                returns {money(refundOnApprove)} to you; disputing holds the deposit until the claim is settled.
              </Typography>
              {state?.defectDocumentHash ? (
                <Typography color="lightWhite" variant="small">
                  Evidence: {state.defectDocumentHash}
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          {/* The deposit dispute. What is offered here depends on the contract's generation: the
              EVM clones settle by two matching proposals, the older TRON ones by GAC's ruling. */}
          {dispute.isDisputed ? (
            <Stack gap={1} className={styles.section}>
              <Typography color="white" fontWeight="600">
                {dispute.supportsProposals
                  ? 'Deposit dispute — two matching proposals settle it'
                  : 'Deposit dispute — under arbitration by GAC'}
              </Typography>
              <Typography color="lightWhite" variant="small">
                {money(dispute.remainingDeposit)} is held by the contract.
              </Typography>
              {dispute.supportsProposals ? (
                <>
                  {dispute.proposals.map((proposal) => (
                    <Typography key={proposal.party} color="lightWhite" variant="small">
                      {proposal.label}{' '}
                      {proposal.rentierAmount === null
                        ? 'have not proposed yet'
                        : `proposed that the dealer keeps ${money(proposal.rentierAmount)}`}
                    </Typography>
                  ))}
                  {dispute.staleClaimAvailableAt && !dispute.canClaimStale ? (
                    <Typography color="lightWhite" variant="small">
                      If nobody agrees, you can claim what is left alone from{' '}
                      {formatDay(dispute.staleClaimAvailableAt)}.
                    </Typography>
                  ) : null}
                </>
              ) : (
                <Typography color="lightWhite" variant="small">
                  The deposit is held until GAC settles the claim.
                </Typography>
              )}
            </Stack>
          ) : null}

          <TxProgress
            state={syncDone ? null : tx.state}
            error={tx.error}
            // No wallet to sign with: `Connect Wallet` and the connection window stand in for Retry (app#315).
            connect={tx.needsWallet && chain ? <WalletButton family={chain.family} /> : null}
            explorerTxUrl={chain?.explorerTxUrl}
            // A real retry: the same action, asked again — not a button that only clears the box.
            onRetry={
              lastAction
                ? () => {
                    tx.reset();
                    setConfirmAction(lastAction);
                  }
                : undefined
            }
          />

          {actionError ? (
            <Typography color="white" variant="small" className={styles.error}>
              {actionError}
            </Typography>
          ) : null}

          {vis.canPay ? (
            <Button
              onClick={() => setPayVisible(true)}
              title="Pay for this rental"
              variant="accent"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : null}

          {vis.canStart && canSignOnchain ? (
            <Button
              onClick={() => ask('start')}
              title="I received the car"
              variant="accent"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : null}

          {vis.canCancel && canSignOnchain ? (
            <Button
              onClick={() => ask('cancel')}
              title="Cancel this rental"
              variant="danger"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : null}

          {vis.canReturn && canSignOnchain ? (
            <Button
              onClick={() => ask('return')}
              title="Return the car"
              variant="accent"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : null}

          {vis.canTopUp ? (
            <Button
              onClick={() => setTopUpVisible(true)}
              title={`${clock.outstandingOverdue > 0n ? 'Settle & top up' : 'Top up deposit'} (${money(
                clock.topUpAmount
              )})`}
              variant="accent"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : null}

          {vis.canRequestExtension ? (
            <>
              <Button
                onClick={() => setExtendVisible(true)}
                title="Extend rental"
                variant={vis.mustTopUpBeforeExtending ? 'extraLightWhite' : 'accent'}
                disabled={busy}
                className={styles.actionButton}
              />
              {vis.mustTopUpBeforeExtending ? (
                <Typography color="lightWhite" variant="small" textAlign="center">
                  Past the end date the dealer can only approve an extension once your deposit is full and nothing is
                  owed for the days already started — settle {money(clock.topUpAmount)} first. The new period then
                  starts where the paid days end, so no day is paid twice.
                </Typography>
              ) : null}
            </>
          ) : vis.extensionPending ? (
            <Button
              onClick={() => {}}
              title={`Extension requested (+${Math.round(Number(state!.pendingExtensionPeriod) / 86_400)}d) — awaiting the dealer`}
              variant="extraLightWhite"
              disabled
              className={styles.actionButton}
            />
          ) : null}

          {vis.defectPending && canSignOnchain ? (
            <>
              <Button
                onClick={() => ask('approveDefect')}
                title="Accept the charge"
                variant="accent"
                disabled={busy}
                className={styles.actionButton}
              />
              <Button
                onClick={() => ask('disputeDefect')}
                title="Dispute the charge"
                variant="extraLightWhite"
                disabled={busy}
                className={styles.actionButton}
              />
            </>
          ) : null}

          {dispute.canPropose && canSignOnchain ? (
            <>
              <Button
                onClick={() => setSplitVisible(true)}
                title="Propose a split"
                variant="accent"
                disabled={busy}
                className={styles.actionButton}
              />
              {dispute.agreeable.map((proposal) => (
                <Button
                  key={proposal.party}
                  onClick={() => ask('agreeSplit', proposal.rentierAmount)}
                  title={`Agree with ${proposal.label} (${money(proposal.rentierAmount)} to the dealer)`}
                  variant="extraLightWhite"
                  disabled={busy}
                  className={styles.actionButton}
                />
              ))}
            </>
          ) : null}

          {dispute.canClaimStale && canSignOnchain ? (
            <Button
              onClick={() => ask('claimStale')}
              title="Claim deposit"
              variant="accent"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : null}

          {vis.canRelease && canSignOnchain ? (
            <Button
              onClick={() => ask('release')}
              title="Release deposit"
              variant="accent"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : vis.inInspection ? (
            <Button
              onClick={() => {}}
              title={`Deposit unlocks in ${formatRemaining(vis.secondsToUnlock)}`}
              variant="extraLightWhite"
              disabled
              className={styles.actionButton}
            />
          ) : null}
        </Stack>
      </Stack>

      {payVisible ? (
        <RentalPayModal
          visible={payVisible}
          onClose={() => setPayVisible(false)}
          rental={item}
          chain={chain}
          onPaid={refetch}
        />
      ) : null}

      {topUpVisible ? (
        <RentalTopUpModal
          visible={topUpVisible}
          onClose={() => setTopUpVisible(false)}
          rental={item}
          chain={chain}
          onToppedUp={refetch}
        />
      ) : null}

      {extendVisible ? (
        <RentalExtendModal
          visible={extendVisible}
          onClose={() => setExtendVisible(false)}
          rental={item}
          chain={chain}
          onRequested={refetch}
        />
      ) : null}

      {splitVisible ? (
        <RentalSplitModal
          visible={splitVisible}
          onClose={() => setSplitVisible(false)}
          rental={item}
          chain={chain}
          remainingDeposit={dispute.remainingDeposit}
          onProposed={refetch}
        />
      ) : null}

      {confirm ? (
        <Modal
          visible={confirmAction !== null}
          onClose={() => setConfirmAction(null)}
          icon={<Car size={64} color="white" weight="fill" />}
          header={confirm.header}
          buttonText={confirm.button}
          buttonVariant={confirm.danger ? 'danger' : 'accent'}
          onSubmit={handleOnchainAction}
        >
          <Typography color="lightWhite" variant="small" textAlign="center">
            {confirm.body}
          </Typography>
          <Button title="Not now" onClick={() => setConfirmAction(null)} />
        </Modal>
      ) : null}
    </div>
  );
};

const PLATE_TONE = {
  success: styles.plateSuccess,
  warning: styles.plateWarning,
  danger: styles.plateDanger,
  muted: styles.plateMuted,
};

const Plate = ({ tone, children }: { tone: keyof typeof PLATE_TONE; children: React.ReactNode }) => (
  <div className={classNames(styles.plate, PLATE_TONE[tone])}>
    <Typography color="white" textAlign="center" variant="small">
      {children}
    </Typography>
  </div>
);
